import React, { useState } from 'react';
import { useAuth } from 'providers/AuthProvider'
import apiClient, { handleAPIError } from 'helpers/apiService';

interface Question {
    competence: string;
    description: string;
    expected_level: string | number;
    field_type: string;
    options?: Record<string, string>;
    min?: number;
    max?: number;
}


const AssessmentBuilder: React.FC<{ onSave: (message: string) => void, onCancel?: () => void }> = ({ onSave, onCancel }) => {

    const auth = useAuth();
    // Initial form state
    const [role, setRole] = useState('')
    const [questions, setQuestions] = useState<Question[]>([]);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);

    const [newQuestion, setNewQuestion] = useState<Question>({
        competence: '',
        description: '',
        expected_level: '',
        field_type: 'choice',
        options: { "Option 1": "" }
    });

    const handleSubmit = async () => {
        const formData = new FormData();
        formData.append('questions', JSON.stringify(questions));
        try {
            const response = await apiClient.post(
                `/v1/skills/admin/assessment/create/${role}`, formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',

                    }
                }
            );

            onSave(response.data['message'])
        } catch (error) {
            console.error('Request failed:', error);
            onSave("Unable to save template to the database");
        }
    };


    const handleRoleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setRole(value);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setNewQuestion({ ...newQuestion, [name]: value });
    };

    const handleOptionChange = (key: string, value: string) => {
        setNewQuestion({
            ...newQuestion,
            options: { ...newQuestion.options, [key]: value }
        });
    };

    const addOption = () => {
        const optionKey = `Option ${Object.keys(newQuestion.options || {}).length + 1}`;
        setNewQuestion({
            ...newQuestion,
            options: { ...newQuestion.options, [optionKey]: '' }
        });
    };

    const handleRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setNewQuestion({
            ...newQuestion,
            [name]: parseInt(value)
        });
    };

    const addQuestion = () => {
        setQuestions([...questions, { ...newQuestion }]);
        resetForm();
    };

    const editQuestion = (index: number) => {
        setEditingIndex(index);
        setNewQuestion(questions[index]);
    };

    const saveQuestion = (index: number) => {
        const updatedQuestions = [...questions];
        updatedQuestions[index] = newQuestion;
        setQuestions(updatedQuestions);
        setEditingIndex(null);
        resetForm();
    };

    const cancelEdit = () => {
        setEditingIndex(null);
        resetForm();
    };

    const deleteQuestion = (index: number) => {
        setQuestions(questions.filter((_, i) => i !== index));
    };

    const resetForm = () => {
        setNewQuestion({
            competence: '',
            description: '',
            expected_level: '',
            field_type: 'choice',
            options: { "Option 1": "" }
        });
    };

    const renderOptionsInput = (disabled: boolean) => (
        <div>
            {Object.entries(newQuestion.options || {}).map(([key, value]) => (
                <div className="form-group ms-4 mt-3 mb-1" key={key}>
                    <label>{key}</label>
                    <input
                        type="text"
                        className="form-control"
                        value={value}
                        onChange={(e) => handleOptionChange(key, e.target.value)}
                    />
                </div>
            ))}
            <button className="btn btn-secondary ms-4 mt-2 mb-1" onClick={addOption} disabled={disabled}>
                Add Option
            </button>
        </div>
    );

    const renderRangeInput = () => (
        <div className="d-flex align-items-center form-group ms-4 mt-3 mb-1" style={{ textAlign: 'right' }}>
            <label className='w-50 me-2'>Min Value</label>
            <input
                type="number"
                name="min"
                className="form-control"
                value={newQuestion.min || ''}
                onChange={handleRangeChange}
            />
            <label className='w-50 me-2'>Max Value</label>
            <input
                type="number"
                name="max"
                className="form-control"
                value={newQuestion.max || ''}
                onChange={handleRangeChange}
            />
        </div>
    );

    return (
        <div className="container">

            <div className="form-group mb-3">
                <label>Role*</label>
                <input
                    type="text"
                    name="role"
                    className="form-control"
                    value={role}
                    onChange={handleRoleInputChange}
                    required
                />
            </div>

            {questions.map((question, index) => (
                <div key={index} className="card mb-2">
                    <div className="card-body">
                        {editingIndex === index ? (
                            <>
                                <div className="form-group">
                                    <label>Competence</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="competence"
                                        value={newQuestion.competence}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Description</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="description"
                                        value={newQuestion.description}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Expected Level</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="expected_level"
                                        value={newQuestion.expected_level}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Field Type</label>
                                    <select
                                        className="form-control"
                                        name="field_type"
                                        value={newQuestion.field_type}
                                        onChange={handleInputChange}
                                    >
                                        <option value="choice">Choice</option>
                                        <option value="range">Range</option>
                                    </select>
                                </div>

                                {newQuestion.field_type === 'choice' && renderOptionsInput(false)}
                                {newQuestion.field_type === 'range' && renderRangeInput()}
                                <div className='mt-2'>
                                    <button className="btn btn-primary me-2" onClick={() => saveQuestion(index)}>
                                        Save
                                    </button>
                                    <button className="btn btn-secondary" onClick={cancelEdit}>
                                        Cancel
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <h5 className="card-title">{question.competence}</h5>
                                <p className="card-text">{question.description}</p>
                                <p className="card-text">
                                    <strong>Expected Level:</strong> {question.expected_level}
                                </p>
                                <p className="card-text">
                                    <strong>Field Type:</strong> {question.field_type}
                                </p>
                                {question.field_type === 'choice' && (
                                    <ul>
                                        {Object.entries(question.options || {}).map(([key, value]) => (
                                            <li key={key}>
                                                <strong>{key}:</strong> {value}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {question.field_type === 'range' && (
                                    <p>
                                        <strong>Range:</strong> {question.min} - {question.max}
                                    </p>
                                )}
                                <button className="btn btn-secondary me-2" onClick={() => editQuestion(index)}
                                    disabled={editingIndex != null}>
                                    Edit
                                </button>
                                <button className="btn btn-danger" onClick={() => deleteQuestion(index)}
                                    disabled={editingIndex != null}>
                                    Delete
                                </button>
                            </>
                        )}
                    </div>
                </div>
            ))}

            <div className="card mb-2">
                <div className="card-body">
                    <div className="form-group mb-2">
                        <label>Competence</label>
                        <input
                            type="text"
                            className="form-control"
                            name="competence"
                            value={newQuestion.competence}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="form-group mb-2">
                        <label>Description</label>
                        <input
                            type="text"
                            className="form-control"
                            name="description"
                            value={newQuestion.description}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="form-group mb-2">
                        <label>Expected Level</label>
                        <input
                            type="text"
                            className="form-control"
                            name="expected_level"
                            value={newQuestion.expected_level}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="form-group mb-2">
                        <label>Field Type</label>
                        <select
                            className="form-control"
                            name="field_type"
                            value={newQuestion.field_type}
                            onChange={handleInputChange}
                        >
                            <option value="choice">Choice</option>
                            <option value="range">Range</option>
                            <option value="text">Text</option>
                        </select>
                    </div>

                    {newQuestion.field_type === 'choice' && renderOptionsInput(editingIndex != null)}
                    {newQuestion.field_type === 'range' && renderRangeInput()}

                </div>
            </div>

            <div className="d-flex align-items-center ms-4 mt-3 mb-1" >

                <div className='flex-grow-1'>
                    <button className="btn btn-primary" onClick={addQuestion} disabled={editingIndex != null}>
                        Add Question
                    </button>
                </div>

                <div className=''>
                    <button className="btn btn-success me-2" onClick={handleSubmit} disabled={editingIndex != null}>
                        Save
                    </button>

                    <button className="btn btn-warning" onClick={onCancel} disabled={editingIndex != null}>
                        Cancel
                    </button>
                </div>
            </div>

            {/* <pre>{JSON.stringify(questions, null, 2)}</pre> */}
        </div>
    );
};

export default AssessmentBuilder;
